import React, { useRef, useEffect, memo, useState } from 'react';
import { LocationItem } from '../../types/search';
import { Box, Typography, CircularProgress, styled, Divider } from '@mui/material';
import { useTranslation } from 'next-i18next';

const DropdownContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  left: 0,
  top: '100%',
  maxHeight: '450px',
  overflowY: 'auto',
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
  zIndex: 1000,
  marginTop: '5px',
  opacity: 0,
  transform: 'translateY(-10px)',
  transition: 'opacity 0.2s ease, transform 0.2s ease',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  '&::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#575758 transparent',
}));

const DropdownItem = styled(Box)(({ theme }) => ({
  padding: '10px 16px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: '#FEF1EA',
    color: '#F4762A',
  },
  transition: 'background-color 0.2s, color 0.2s',
}));

const ItemContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));

const ItemDetails = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#777',
  marginTop: '4px',
  fontStyle: 'normal',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '1.2',
  opacity: 0.9,
  fontWeight: 400,
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
}));

const NoResultsMessage = styled(Box)(({ theme }) => ({
  padding: '20px',
  textAlign: 'center',
  color: '#666',
}));

const LocationPrefix = styled('span')(({ theme }) => ({
  color: '#F4762A',
  fontWeight: 'bold',
  marginRight: '4px',
}));

const ResultsHeader = styled(Box)(({ theme }) => ({
  padding: '8px 16px', 
  borderBottom: '1px solid #eee', 
  fontSize: '14px', 
  color: '#333',
  fontWeight: 500,
  backgroundColor: '#f9f9f9',
}));

class LocationCache {
  private capacity: number;
  private cache: Map<string, { data: LocationItem[], timestamp: number }>;
  private ttl: number = 86400000;
  
  constructor(capacity = 50) {
    this.capacity = capacity;
    this.cache = new Map();
  }
  
  get(key: string): LocationItem[] | undefined {
    if (!this.cache.has(key)) return undefined;
    
    const cachedData = this.cache.get(key)!;
    
    if (Date.now() - cachedData.timestamp > this.ttl) {
      this.cache.delete(key);
      return undefined;
    }
    
    this.cache.delete(key);
    this.cache.set(key, cachedData);
    return cachedData.data;
  }
  
  set(key: string, value: LocationItem[]): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }
    
    if (this.cache.size >= this.capacity) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
    
    this.cache.set(key, { data: value, timestamp: Date.now() });
  }
  
  has(key: string): boolean {
    if (!this.cache.has(key)) return false;
    
    const cachedData = this.cache.get(key)!;
    if (Date.now() - cachedData.timestamp > this.ttl) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }
}

const locationCache = new LocationCache(100);

interface SearchDropdownProps {
  isVisible: boolean;
  items: LocationItem[];
  loading: boolean;
  searchText: string;
  onItemClick: (item: LocationItem) => void;
  anchorEl: HTMLElement | null;
  hasMore: boolean;
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  onClose?: () => void;
}

const SearchDropdown: React.FC<SearchDropdownProps> = memo(({
  isVisible,
  items,
  loading,
  searchText,
  onItemClick,
  anchorEl,
  hasMore,
  onScroll,
  onClose
}) => {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language || 'th';
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  
  const getLocalizedName = (item: LocationItem) => {
    if ((item as any).nameTH || (item as any).nameEN || (item as any).nameCN) {
      return locale === 'th' ? ((item as any).nameTH || item.label) : 
             locale === 'en' ? ((item as any).nameEN || item.label) : 
             ((item as any).nameCN || item.label);
    }
    return item.originalLabel || item.label;
  };

  const getLocalizedParentName = (item: LocationItem) => {
    if ((item as any).parentNameTH || (item as any).parentNameEN || (item as any).parentNameCN) {
      return locale === 'th' ? ((item as any).parentNameTH || item.parentName) : 
             locale === 'en' ? ((item as any).parentNameEN || item.parentName) : 
             ((item as any).parentNameCN || item.parentName);
    }
    return item.parentName;
  };

  const getLocalizedTypeName = (item: LocationItem) => {
    if ((item as any).typeNameTH || (item as any).typeNameEN || (item as any).typeNameCN) {
      return locale === 'th' ? ((item as any).typeNameTH || item.typeName) : 
             locale === 'en' ? ((item as any).typeNameEN || item.typeName) : 
             ((item as any).typeNameCN || item.typeName);
    }
    return item.typeName;
  };

  useEffect(() => {
    if (!items || items.length === 0) {
      return;
    }

    const nonProjects = items.filter(item => item.typeId !== -1);

    if (nonProjects.length > 0 && searchText && searchText.length > 0) {
      const cacheKey = `location_${searchText.toLowerCase()}`;
      locationCache.set(cacheKey, nonProjects);
    }
  }, [items, searchText]);

  useEffect(() => {
    if (isVisible && dropdownRef.current) {
      setTimeout(() => {
        if (dropdownRef.current) {
          dropdownRef.current.classList.add('visible');
        }
      }, 10);
    } else if (dropdownRef.current) {
      dropdownRef.current.classList.remove('visible');
    }
  }, [isVisible]);

  useEffect(() => {
    if (!anchorEl || !dropdownRef.current) return;

    const searchContainer = anchorEl.closest('div[class*="MuiInputBase-root"]') || anchorEl.parentElement;
    
    if (searchContainer) {
      const rect = searchContainer.getBoundingClientRect();
      const dropdown = dropdownRef.current;
      
      dropdown.style.width = `${rect.width}px`;
    }
  }, [anchorEl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target as Node) && 
          anchorEl && 
          !anchorEl.contains(event.target as Node) &&
          onClose) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, anchorEl, onClose]);

  if (!isVisible) return null;

  const renderItems = () => {
    if (items.length === 0) return null;

    const isProject = (item: LocationItem) => item.typeId === -1;

    return (
      <>
        {items.map((item, idx) => (
          <React.Fragment key={`item-${item.typeId}-${idx}`}>
            {isProject(item) && idx > 0 && !isProject(items[idx-1]) && (
              <div data-section="โครงการ"></div>
            )}
            
            <DropdownItem onClick={() => onItemClick(item)}>
              <ItemContent>
                <Box sx={{ flex: 1 }}>
                  {renderLocationName(item)}
                </Box>
              </ItemContent>
              {renderItemDetails(item)}
            </DropdownItem>
            {idx < items.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </>
    );
  };

  const renderLocationName = (item: LocationItem) => {
    if (item.rendered) {
      return (
        <span dangerouslySetInnerHTML={{ __html: item.displayLabel || item.label }} />
      );
    }
    
    return (
      <Typography component="span" sx={{ 
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '125%',
        letterSpacing: '0%'
      }}>
        {getLocalizedName(item)}
      </Typography>
    );
  };

  const renderItemDetails = (item: LocationItem) => {
    const isDistrictOrSubDistrict = (item: LocationItem) => {
      if (item.typeName) {
        return item.typeName.includes('อำเภอ') || 
               item.typeName.includes('ตำบล') ||
               item.typeName.includes('District') ||
               item.typeName.includes('Sub-district');
      }
      
      return item.isAmphur || item.isDistrict;
    };

    if (isDistrictOrSubDistrict(item)) {
      if (item.parentName && item.parentName.trim() !== '') {
        return (
          <ItemDetails>
            <span>{getLocalizedParentName(item)}</span>
          </ItemDetails>
        );
      } else {
        return null;
      }
    }

    if (item.typeName) {
      // สำหรับโครงการ (typeId === -1) ไม่แสดง parentName
      if (item.typeId === -1) {
        return (
          <ItemDetails>
            <span>{getLocalizedTypeName(item)}</span>
          </ItemDetails>
        );
      }
      
      if (item.parentName && item.parentName.trim() !== '') {
        return (
          <ItemDetails>
            <span>{getLocalizedTypeName(item)} &gt; {getLocalizedParentName(item)}</span>
          </ItemDetails>
        );
      } else {
        return (
          <ItemDetails>
            <span>{getLocalizedTypeName(item)}</span>
          </ItemDetails>
        );
      }
    }
    
    let typeText = '';
    
    if (item.desc) {
      if (item.typeId === 0) {
        return (
          <ItemDetails>
            {item.desc}
          </ItemDetails>
        );
      }
      
      if (item.typeId === 10) {
        typeText = t('location_categories.mass_transit');
      } else if (item.typeId === 11) {
        typeText = t('location_categories.shopping_mall');
      } else if (item.typeId === 17) {
        typeText = t('location_categories.university');
      } else if (item.typeId === 20) {
        typeText = t('location_categories.road');
      } else if (item.typeId === 9) {
        typeText = t('location_categories.hospital');
      } else if (item.typeId === 8) {
        typeText = t('location_categories.park');
      } else if (item.typeId === 1) {
        typeText = t('location_categories.airport');
      } else if (item.typeId === 2) {
        typeText = t('location_categories.popular_location');
      } else {
        typeText = '';
      }
      
      return (
        <ItemDetails>
          {typeText && <span>{typeText} &gt; {item.desc}</span>}
        </ItemDetails>
      );
    }
    
    if (item.typeId === 0) {
      typeText = t('location_categories.province');
    } else if (item.typeId === -1) {
      typeText = t('project');
    } else if (item.typeId === 10) {
      typeText = t('location_categories.mass_transit');
    } else if (item.typeId === 11) {
      typeText = t('location_categories.shopping_mall');
    } else if (item.typeId === 17) {
      typeText = t('location_categories.university');
    } else if (item.typeId === 20) {
      typeText = t('location_categories.road');
    } else if (item.typeId === 9) {
      typeText = t('location_categories.hospital');
    } else if (item.typeId === 8) {
      typeText = t('location_categories.park');
    } else if (item.typeId === 1) {
      typeText = t('location_categories.airport');
    } else if (item.typeId === 2) {
      typeText = t('location_categories.popular_location');
    }
    
    return (
      <ItemDetails>
        {typeText && <span>{typeText}</span>}
      </ItemDetails>
    );
  };

  return (
    <DropdownContainer ref={dropdownRef} onScroll={onScroll}>
      {loading && items.length === 0 ? (
        <LoadingContainer>
          <CircularProgress size={30} style={{ color: '#F4762A' }} />
        </LoadingContainer>
      ) : items.length === 0 ? (
        <NoResultsMessage>
          {t('no_results_for', {defaultValue: 'ไม่พบผลลัพธ์สำหรับ'}) + ` "${searchText}"`}
        </NoResultsMessage>
      ) : (
        <>
          {renderItems()}
        </>
      )}
      {loading && items.length > 0 && (
        <LoadingContainer>
          <CircularProgress size={24} style={{ color: '#F4762A' }} />
        </LoadingContainer>
      )}
      {hasMore && !loading && items.length > 0 && (
        <Box sx={{ textAlign: 'center', padding: '10px', fontSize: '13px', color: '#777' }}>
          {t('scroll_down_for_more', {defaultValue: 'เลื่อนลงเพื่อดูเพิ่มเติม'})}
        </Box>
      )}
    </DropdownContainer>
  );
});

export default SearchDropdown; 